import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import SectionHeader from '../../components/Profile/SectionHeader'
import type { ProCardViewModel } from './types'

function formatPixeWhole(value?: number | null): string | null {
  if (value == null) return null
  if (!Number.isFinite(value)) return null
  if (Number.isInteger(value)) return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value)
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)
}

function formatPixeAtomic(amount: bigint): string {
  const scale = 1000000000000n
  const whole = amount / scale
  const frac = amount % scale
  if (frac === 0n) return whole.toLocaleString('en-US')
  const fracStr = frac.toString().padStart(12, '0').replace(/0+$/, '')
  return `${whole.toLocaleString('en-US')}.${fracStr}`
}

export default function ProPublicCard({
  pro,
  onEdit,
  show_book_cta = true,
  variant = 'profile',
  right_actions,
}: {
  pro: ProCardViewModel
  onEdit?: () => void
  show_book_cta?: boolean
  variant?: 'profile' | 'preview' | 'browse'
  right_actions?: ReactNode
}) {
  const isOwner = Boolean(pro.is_owner)
  const hourly = formatPixeWhole(pro.hourly_rate_pixe)
  const base = formatPixeWhole(pro.starting_price_pixe)
  const bio = String(pro.description || '').trim()
  const shortBio = bio.length > 200 ? `${bio.slice(0, 200)}...` : bio
  const skills = Array.isArray(pro.skills) ? pro.skills.map((s) => String(s || '').trim()).filter(Boolean) : []
  const visibleSkills = skills.slice(0, 5)
  const overflowSkills = Math.max(0, skills.length - visibleSkills.length)
  const ratingCount = Number(pro.rating_count || 0)
  const averageRating = Number(pro.rating_avg || 0)
  const completedJobsCount = pro.jobs_count != null ? Number(pro.jobs_count) : null
  const proModeLabel = pro.mode === 'onsite' ? 'On-site' : 'Remote'

  let totalEarned = 0n
  try {
    totalEarned = BigInt(pro.earned_pixe == null ? '0' : String(pro.earned_pixe))
  } catch {
    totalEarned = 0n
  }

  const sectionClass = variant === 'preview' ? '' : variant === 'browse' ? '' : 'mb-8'

  return (
    <section className={sectionClass} aria-label="Pixe Pros">
      {show_book_cta ? (
        <SectionHeader
          title="Pixe Pros"
          subtitle="Book this pro for remote or local work."
          rightActions={
            isOwner ? (
              <button
                type="button"
                onClick={onEdit}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-sm font-semibold hover:bg-white/10"
              >
                Edit
              </button>
            ) : null
          }
        />
      ) : null}

      <div className={`rounded-2xl border border-indigo-400/20 bg-[radial-gradient(950px_380px_at_10%_0%,rgba(99,102,241,0.20),transparent_58%),radial-gradient(950px_380px_at_100%_20%,rgba(56,189,248,0.14),transparent_58%)] ${variant === 'browse' ? 'p-4' : 'p-5'}`}>
        {variant === 'browse' ? (
          <div className="mb-3 flex items-center gap-3">
            {pro.avatar_url ? (
              <img src={pro.avatar_url} alt={pro.display_name || pro.handle || 'Pro'} className="h-11 w-11 rounded-full object-cover" />
            ) : (
              <div className="grid h-11 w-11 place-items-center rounded-full bg-white/10 text-sm font-semibold text-white/80">
                {(pro.display_name || pro.handle || 'P').slice(0, 1).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold text-white">{pro.display_name || pro.handle || 'Pro'}</div>
              <div className="truncate text-xs text-white/60">@{pro.handle || 'pro'}</div>
            </div>
          </div>
        ) : null}
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            {pro.title ? <div className="text-base font-semibold">{pro.title}</div> : null}
            {shortBio ? <p className="mt-1 text-sm text-white/80 line-clamp-2">{shortBio}</p> : null}

            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              {hourly ? <span className="rounded-full border border-white/15 px-2 py-1 bg-black/30">{hourly} PIXE/hr</span> : null}
              {!hourly && base ? <span className="rounded-full border border-white/15 px-2 py-1 bg-black/30">From {base} PIXE</span> : null}
              <span className="rounded-full border border-white/15 px-2 py-1 bg-black/30">{proModeLabel}</span>
              {pro.pros_to_go_enabled ? <span className="rounded-full border border-sky-400/30 px-2 py-1 bg-sky-500/10 text-sky-100">Pros to Go</span> : null}
              {pro.company_title ? (
                pro.company_slug ? (
                  <Link
                    to={`/b/${pro.company_slug}`}
                    className="rounded-full border border-cyan-300/35 bg-cyan-500/10 px-2 py-1 text-cyan-100 hover:bg-cyan-500/20"
                  >
                    Company: {pro.company_title}
                  </Link>
                ) : (
                  <span className="rounded-full border border-cyan-300/35 bg-cyan-500/10 px-2 py-1 text-cyan-100">
                    Company: {pro.company_title}
                  </span>
                )
              ) : null}
              <span className={`rounded-full border px-2 py-1 ${pro.available_now ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-100' : 'border-white/15 bg-black/30 text-white/80'}`}>
                {pro.available_now ? 'Available now' : 'Not available'}
              </span>
            </div>

            {pro.company_title && pro.company_role_title ? (
              <div className="mt-1 text-xs text-white/65">{pro.company_role_title} at {pro.company_title}</div>
            ) : null}

            {(ratingCount > 0 && averageRating > 0) || completedJobsCount != null || totalEarned > 0n || isOwner ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                {ratingCount > 0 && averageRating > 0 ? (
                  <span className="inline-flex items-center gap-1">
                    <svg viewBox="0 0 24 24" className="h-4 w-4 fill-yellow-400 text-yellow-400" aria-hidden="true">
                      <path d="M12 17.27 18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                    </svg>
                    <span className="font-semibold text-yellow-200">{averageRating.toFixed(1)}</span>
                    <span className="text-white/55">({ratingCount} reviews)</span>
                  </span>
                ) : isOwner ? <span className="text-white/55">No reviews yet</span> : null}

                {(ratingCount > 0 && averageRating > 0) || isOwner ? completedJobsCount != null || totalEarned > 0n ? <span className="text-white/35">|</span> : null : null}
                {completedJobsCount != null ? <span className="text-white/65">{completedJobsCount} jobs</span> : null}
                {completedJobsCount != null && totalEarned > 0n ? <span className="text-white/35">|</span> : null}
                {totalEarned > 0n ? <span className="text-white/65">{formatPixeAtomic(totalEarned)} PIXE earned</span> : null}
              </div>
            ) : null}
          </div>

          {right_actions ? <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{right_actions}</div> : null}
        </div>

        {skills.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {visibleSkills.map((s) => (
              <span key={s} className="rounded-full border border-white/12 bg-black/30 px-2 py-0.5 text-[11px] text-white/80">
                {s}
              </span>
            ))}
            {overflowSkills > 0 ? <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-[11px] text-white/60">+{overflowSkills}</span> : null}
          </div>
        ) : null}
      </div>
    </section>
  )
}
